import type { Location, Reference } from '@medplum/fhirtypes'
import { ExtendedLocation } from '@seen/core/models/location'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { IconBuilding } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useRead } from '#src/hooks/useRead.ts'
import { useReference } from '#src/hooks/useReference.ts'

export function LocationNameplate({
  location,
}: {
  location: Reference<Location> | string
}): JSX.Element {
  const locationReference = useReference(location)
  const { data: instance } = useRead(ExtendedLocation, locationReference)
  const name = instance?.name
  const address = instance?.addressString
  // The managing organization identifies whose place this is (practice, ASC,
  // hospital) — the strongest cue for disambiguating a bare address. Astrana-
  // imported offices have no facility name, so `name` is just the address.
  const { data: organization } = useRead(
    ExtendedOrganization,
    instance?.managingOrganization
  )
  const organizationName = organization?.name
  const isSame =
    name?.toLowerCase().replace(/\W/g, '') ===
    address?.toLowerCase().replace(/\W/g, '')
  // Don't repeat the org name when it's already the location's own name.
  const showOrganization =
    !!organizationName &&
    organizationName.toLowerCase().replace(/\W/g, '') !==
      name?.toLowerCase().replace(/\W/g, '')

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        <p className="align-start min-w-0 text-sm text-wrap wrap-break-word">
          {name}
        </p>
        {!isSame && (
          <p className="min-w-0 text-xs text-wrap wrap-break-word text-muted-foreground">
            {address}
          </p>
        )}
      </div>
      {showOrganization && (
        <p className="flex min-w-0 items-center gap-1 text-xs text-wrap wrap-break-word text-muted-foreground">
          <IconBuilding className="size-3 shrink-0" />
          {organizationName}
        </p>
      )}
    </div>
  )
}
